import { canUsePhoto } from "./rights";
import type { AppData, CosplayProject } from "./types";

export type ProjectEvidenceStatus = "ready" | "attention" | "blocked" | "unknown" | "provider_off" | "not_required";
export type ProjectCommandState = "ready" | "at_risk" | "blocked";
export type ProjectLedgerId = "wardrobe" | "market" | "events" | "shoot" | "photos";

export interface ProjectLedgerDecision {
  id: ProjectLedgerId;
  label: string;
  status: ProjectEvidenceStatus;
  summary: string;
  evidenceCount: number;
  evidenceTotal: number;
  requiredNow: boolean;
  route: string;
  actionLabel: string;
  blocker?: string;
}

export interface ProjectBudgetDecision {
  limitKrw: number;
  plannedKrw: number;
  committedKrw: null;
  remainingPlannedKrw: number;
  status: "within_plan" | "over_plan" | "unknown_committed";
  summary: string;
}

export interface ProjectCommandDecision {
  projectId: string;
  state: ProjectCommandState;
  dDay: {
    status: "future" | "today" | "past" | "unknown";
    daysRemaining: number | null;
    label: string;
  };
  taskProgress: {
    done: number;
    total: number;
    percent: number;
  };
  evidenceProgress: {
    ready: number;
    required: number;
  };
  budget: ProjectBudgetDecision;
  rights: {
    status: ProjectEvidenceStatus;
    ready: number;
    total: number;
    summary: string;
  };
  reuse: {
    ready: number;
    needsCheck: number;
    summary: string;
    route: "/wardrobe";
  };
  ledgers: ProjectLedgerDecision[];
  blockers: Array<{
    id: string;
    severity: "blocking" | "attention" | "external";
    label: string;
    route: string;
  }>;
  nextAction: {
    route: string;
    label: string;
    reason: string;
  };
  providerBoundaries: string[];
}

const DAY_MS = 24 * 60 * 60 * 1_000;

function parseCalendarDate(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) return null;
  return timestamp;
}

function deriveDDay(value: string, today: Date): ProjectCommandDecision["dDay"] {
  const deadline = parseCalendarDate(value);
  if (deadline === null || Number.isNaN(today.getTime())) {
    return { status: "unknown", daysRemaining: null, label: value ? "날짜 확인 필요" : "아직 미정" };
  }
  const todayStart = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const daysRemaining = Math.round((deadline - todayStart) / DAY_MS);
  if (daysRemaining > 0) return { status: "future", daysRemaining, label: `${daysRemaining}일 전` };
  if (daysRemaining === 0) return { status: "today", daysRemaining, label: "오늘" };
  return { status: "past", daysRemaining, label: `${Math.abs(daysRemaining)}일 지남` };
}

const ledgerDecision = (decision: ProjectLedgerDecision) => decision;

export function deriveProjectCommand(data: AppData, project: CosplayProject, today = new Date()): ProjectCommandDecision {
  const dDay = deriveDDay(project.dDay, today);
  const wardrobe = data.wardrobe.filter((item) => item.projectId === project.id);
  const activeMarketDrafts = data.marketDrafts.filter((draft) => draft.projectId === project.id && draft.status !== "cancelled");
  const eventTasks = project.tasks.filter((task) => task.id.startsWith("event-"));
  const shootPlans = data.shootPlans.filter((plan) => plan.projectId === project.id);
  const activeBookings = data.bookingDrafts.filter((draft) => draft.projectId === project.id && draft.status !== "cancelled");
  const recruitments = data.recruitmentDrafts.filter((draft) => draft.projectId === project.id);
  const photos = data.photos.filter((photo) => photo.projectId === project.id);

  const readyWardrobe = wardrobe.filter((item) => item.ready).length;
  const wardrobeDecision = ledgerDecision({
    id: "wardrobe",
    label: "의상 파츠",
    status: wardrobe.length === 0 ? "unknown" : readyWardrobe === wardrobe.length ? "ready" : "attention",
    summary: wardrobe.length === 0 ? "등록된 파츠가 없어요." : `${wardrobe.length}개 중 ${readyWardrobe}개 실물 확인`,
    evidenceCount: readyWardrobe,
    evidenceTotal: wardrobe.length,
    requiredNow: true,
    route: "/wardrobe",
    actionLabel: wardrobe.length === 0 ? "파츠 만들기" : "파츠 확인",
    blocker: wardrobe.length === 0 ? "의상 파츠를 아직 기록하지 않았어요." : readyWardrobe < wardrobe.length ? `${wardrobe.length - readyWardrobe}개 파츠의 실물·실측 확인이 남았어요.` : undefined,
  });

  const marketDecision = ledgerDecision({
    id: "market",
    label: "거래 초안",
    status: activeMarketDrafts.length > 0 ? "provider_off" : "not_required",
    summary: activeMarketDrafts.length > 0 ? `${activeMarketDrafts.length}개 초안 · 외부 공개와 결제 0건` : "필요할 때만 초안을 만들어요.",
    evidenceCount: 0,
    evidenceTotal: activeMarketDrafts.length,
    requiredNow: false,
    route: "/market",
    actionLabel: activeMarketDrafts.length > 0 ? "초안 확인" : "거래 초안 열기",
    blocker: activeMarketDrafts.length > 0 ? "초안은 저장됐지만 공개·결제·정산은 연결 전이에요." : undefined,
  });

  const doneEventTasks = eventTasks.filter((task) => task.status === "done").length;
  const eventDecision = ledgerDecision({
    id: "events",
    label: "행사 규칙",
    status: eventTasks.length === 0 ? "unknown" : doneEventTasks === eventTasks.length ? "ready" : "attention",
    summary: eventTasks.length === 0 ? "행사별 공식 규칙을 아직 붙이지 않았어요." : `${eventTasks.length}개 중 ${doneEventTasks}개 공식 규칙 확인`,
    evidenceCount: doneEventTasks,
    evidenceTotal: eventTasks.length,
    requiredNow: true,
    route: "/events",
    actionLabel: eventTasks.length === 0 ? "행사 고르기" : "규칙 확인",
    blocker: eventTasks.length === 0 ? "선택한 행사의 반입·촬영 규칙 근거가 없어요." : doneEventTasks < eventTasks.length ? `${eventTasks.length - doneEventTasks}개 공식 규칙을 확인해야 해요.` : undefined,
  });

  const latestShoot = shootPlans[0];
  const shootReady = Boolean(latestShoot && latestShoot.slots.length > 0);
  const shootDecision = ledgerDecision({
    id: "shoot",
    label: "함께 촬영 일정",
    status: shootReady ? "ready" : "unknown",
    summary: shootReady
      ? `${latestShoot?.slots.length ?? 0}개 슬롯 · ${latestShoot?.members.length ?? 0}명 배치${recruitments.length > 0 ? ` · 모집 초안 ${recruitments.length}개` : ""}${activeBookings.length > 0 ? ` · 예약 초안 ${activeBookings.length}건(외부 전송 0)` : ""}`
      : "시간·멤버·촬영자 배치가 아직 없어요.",
    evidenceCount: shootReady ? 1 : 0,
    evidenceTotal: 1,
    requiredNow: true,
    route: "/shoot",
    actionLabel: shootReady ? "촬영표 확인" : "촬영표 만들기",
    blocker: shootReady ? undefined : "현장에서 쓸 촬영표가 없어요.",
  });

  const rightsReady = photos.filter((photo) => canUsePhoto(photo, "portfolio")).length;
  const photosRequiredNow = dDay.status === "today" || dDay.status === "past" || photos.length > 0;
  const photoStatus: ProjectEvidenceStatus = photos.length === 0
    ? photosRequiredNow ? "unknown" : "not_required"
    : rightsReady === photos.length ? "ready" : rightsReady === 0 ? "blocked" : "attention";
  const photoDecision = ledgerDecision({
    id: "photos",
    label: "사진·공개 범위",
    status: photoStatus,
    summary: photos.length === 0
      ? photosRequiredNow ? "촬영 뒤 받은 사진 기록이 없어요." : "촬영 뒤 보정·동의를 확인해요."
      : `${photos.length}개 중 ${rightsReady}개 보정·포트폴리오 동의 확인`,
    evidenceCount: rightsReady,
    evidenceTotal: photos.length,
    requiredNow: photosRequiredNow,
    route: "/photos",
    actionLabel: photos.length === 0 ? "사진 기록 열기" : "권리 확인",
    blocker: photoStatus === "blocked" || photoStatus === "attention" || photoStatus === "unknown"
      ? photos.length === 0 ? "촬영 후 사진 수신 기록이 필요해요." : `${photos.length - rightsReady}개 사진의 보정 승인·포트폴리오 동의가 남았어요.`
      : undefined,
  });

  const plannedWardrobeKrw = wardrobe.reduce((sum, item) => sum + Math.max(0, item.budgetKrw), 0);
  const plannedBookingKrw = activeBookings.reduce((sum, draft) => sum + Math.max(0, draft.priceKrw), 0);
  const plannedKrw = plannedWardrobeKrw + plannedBookingKrw;
  const limitKrw = Math.max(0, project.budgetLimitKrw ?? project.preferences.budgetKrw);
  const remainingPlannedKrw = limitKrw - plannedKrw;
  const overPlan = remainingPlannedKrw < 0;
  const budget: ProjectBudgetDecision = {
    limitKrw,
    plannedKrw,
    committedKrw: null,
    remainingPlannedKrw,
    status: overPlan ? "over_plan" : "unknown_committed",
    summary: limitKrw === 0
      ? "예산 한도와 파츠별 예상액을 아직 입력하지 않았어요."
      : overPlan
      ? `예상액이 한도보다 ${Math.abs(remainingPlannedKrw).toLocaleString("ko-KR")}원 많아요. 실지출은 아직 몰라요.`
      : `예상액은 한도 안이에요. 영수증·실결제 기록이 없어 실지출은 아직 몰라요.`,
  };

  const ledgers = [wardrobeDecision, marketDecision, eventDecision, shootDecision, photoDecision];
  const requiredLedgers = ledgers.filter((ledger) => ledger.requiredNow);
  const evidenceProgress = {
    ready: requiredLedgers.filter((ledger) => ledger.status === "ready").length,
    required: requiredLedgers.length,
  };
  const taskDone = project.tasks.filter((task) => task.status === "done").length;
  const taskProgress = {
    done: taskDone,
    total: project.tasks.length,
    percent: project.tasks.length === 0 ? 0 : Math.round((taskDone / project.tasks.length) * 100),
  };

  const hasBlockingLedger = requiredLedgers.some((ledger) => ledger.status === "blocked");
  const hasUnverifiedLedger = requiredLedgers.some((ledger) => ledger.status !== "ready");
  const state: ProjectCommandState = overPlan || hasBlockingLedger ? "blocked" : dDay.status === "unknown" || hasUnverifiedLedger ? "at_risk" : "ready";

  const blockers: ProjectCommandDecision["blockers"] = [];
  if (overPlan) blockers.push({ id: "budget-over-plan", severity: "blocking", label: budget.summary, route: "/wardrobe" });
  for (const ledger of requiredLedgers) {
    if (ledger.status === "ready" || ledger.status === "not_required") continue;
    blockers.push({
      id: `ledger-${ledger.id}`,
      severity: ledger.status === "blocked" ? "blocking" : "attention",
      label: ledger.blocker ?? ledger.summary,
      route: ledger.route,
    });
  }
  if (marketDecision.status === "provider_off") blockers.push({ id: "market-provider-off", severity: "external", label: marketDecision.blocker ?? marketDecision.summary, route: "/market" });
  if (activeBookings.length > 0) blockers.push({ id: "booking-provider-off", severity: "external", label: `촬영 예약 ${activeBookings.length}건은 로컬 초안이며 외부 전송·결제 0건이에요.`, route: "/shoot" });

  const firstRequiredGap = requiredLedgers.find((ledger) => ledger.status !== "ready");
  const nextAction = overPlan
    ? { route: "/wardrobe", label: "예상 비용 줄이기", reason: budget.summary }
    : firstRequiredGap
      ? { route: firstRequiredGap.route, label: firstRequiredGap.actionLabel, reason: firstRequiredGap.blocker ?? firstRequiredGap.summary }
      : { route: "/shoot", label: "현장 전 촬영표 확인", reason: "필수 준비 근거가 모였어요. 현장에서 쓸 순서만 마지막으로 확인해요." };

  const providerBoundaries = ["기본 준비표는 무료예요. 기록만으로 구매나 예약이 신청되지 않아요."];
  if (activeMarketDrafts.length > 0) providerBoundaries.push("거래 초안은 개인 메모예요. 판매자에게 직접 확인하고 전달해야 해요.");
  if (activeBookings.length > 0) providerBoundaries.push("촬영 요청서는 작성 중인 메모예요. 촬영자와 별도로 일정을 확정해 주세요.");
  const reusableWardrobe = wardrobe.filter((item) => item.status === "owned");
  const reusableReady = reusableWardrobe.filter((item) => item.ready).length;
  const reusableNeedsCheck = reusableWardrobe.length - reusableReady;

  return {
    projectId: project.id,
    state,
    dDay,
    taskProgress,
    evidenceProgress,
    budget,
    rights: {
      status: photoDecision.status,
      ready: rightsReady,
      total: photos.length,
      summary: photoDecision.summary,
    },
    reuse: {
      ready: reusableReady,
      needsCheck: reusableNeedsCheck,
      summary: reusableReady > 0
        ? `${reusableReady}개 보유 파츠를 다시 쓸 수 있어요.${reusableNeedsCheck > 0 ? ` ${reusableNeedsCheck}개는 상태 확인이 필요해요.` : ""}`
        : reusableNeedsCheck > 0 ? `${reusableNeedsCheck}개 보유 파츠는 상태를 확인하면 다시 쓸 수 있어요.` : "재사용할 보유 파츠를 아직 표시하지 않았어요.",
      route: "/wardrobe",
    },
    ledgers,
    blockers,
    nextAction,
    providerBoundaries,
  };
}
